import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/config/firebase';
import {
    ExclamationTriangleIcon,
    TrashIcon,
    CheckCircleIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';

const CONFIRMATION_TEXT = 'APAGAR TODOS OS USUARIOS';

const DeleteAuthUsersMigration: React.FC = () => {
    const [confirmationInput, setConfirmationInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<{ success: boolean; count?: number; error?: string } | null>(null);

    const isButtonDisabled = confirmationInput !== CONFIRMATION_TEXT || isLoading;

    const handleExecute = async () => {
        if (confirmationInput !== CONFIRMATION_TEXT) return;

        setIsLoading(true);
        setResult(null);

        try {
            const functions = getFunctions(app, 'us-central1');
            const deleteAllAuthUsers = httpsCallable(functions, 'deleteAllAuthUsers');

            const response = await deleteAllAuthUsers();
            const data = response.data as { success: boolean; count: number };

            setResult({ success: true, count: data.count });
            setConfirmationInput(''); // Reset so they don't double click
        } catch (error: any) {
            console.error("Erro ao deletar usuários do Auth:", error);
            setResult({ success: false, error: error.message || 'Ocorreu um erro desconhecido.' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-500" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                            Ação Extremamente Destrutiva
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                            <p>
                                Esta operação irá apagar <strong>todos os usuários</strong> do Firebase Authentication, revogando o acesso ao sistema para todos os colaboradores. Eles precisarão realizar um novo login (via Microsoft/Google) para que suas contas de autenticação sejam recriadas (os perfis no Firestore serão mantidos).
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {!result?.success ? (
                <div className="bg-white px-4 py-5 border-t border-gray-200 sm:px-6">
                    <div className="space-y-4 max-w-xl">
                        <div>
                            <label htmlFor="confirm-delete" className="block text-sm font-medium text-gray-700">
                                Para confirmar a exclusão, digite: <span className="font-mono bg-gray-100 p-1 rounded font-bold">{CONFIRMATION_TEXT}</span>
                            </label>
                            <div className="mt-1">
                                <input
                                    type="text"
                                    name="confirm-delete"
                                    id="confirm-delete"
                                    className="shadow-sm focus:ring-red-500 focus:border-red-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                                    placeholder={CONFIRMATION_TEXT}
                                    value={confirmationInput}
                                    onChange={(e) => setConfirmationInput(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end pt-4">
                            <button
                                type="button"
                                onClick={handleExecute}
                                disabled={isButtonDisabled}
                                className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white transition-colors duration-200
                  ${isButtonDisabled
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                    }`}
                            >
                                {isLoading ? (
                                    <>
                                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                                        Processando lote...
                                    </>
                                ) : (
                                    <>
                                        <TrashIcon className="h-4 w-4 mr-2" />
                                        Apagar Todos os Usuários
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {result?.error && (
                        <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
                            <p className="text-sm text-red-700 font-bold">Falha:</p>
                            <p className="text-sm text-red-600">{result.error}</p>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-green-50 border border-green-200 rounded-md p-6 flex flex-col items-center justify-center text-center space-y-4">
                    <CheckCircleIcon className="h-16 w-16 text-green-500" />
                    <div>
                        <h3 className="text-lg font-medium text-green-800">Operação Concluída com Sucesso</h3>
                        <p className="text-sm text-green-600 mt-2">
                            Foram apagados <strong>{result.count}</strong> usuários do Firebase Authentication.
                        </p>
                    </div>
                    <button
                        onClick={() => setResult(null)}
                        className="mt-4 text-sm text-green-700 hover:text-green-800 underline font-medium"
                    >
                        Realizar nova limpeza
                    </button>
                </div>
            )}
        </div>
    );
};

export default DeleteAuthUsersMigration;
